"use client";

import { cva, type VariantProps } from "cva";
import { cn } from "#ui-atoms/lib/utils";
import {
  BaseCard,
  BaseCardDescription,
  BaseCardHeader,
  BaseCardPanel,
  type BaseCardProps,
  BaseCardTitle,
} from "./card";
import type { CardParts } from ".";

const conEdisonCardVariants = cva({
  base: "m-0 w-full max-w-[91rem] rounded-none border-none bg-white text-inherit shadow-none",
});

function ConEdisonCard({ className, ...props }: BaseCardProps) {
  return (
    <BaseCard
      {...props}
      className={cn(
        conEdisonCardVariants({
          className,
        }),
      )}
    />
  );
}

const conEdisonCardHeaderVariants = cva({
  base: "space-y-5 border-tenant-con-edison-website-border px-5 pb-0 pt-16 md:px-10",
  variants: {
    centered: { true: "text-center", false: "text-left" },
  },
  compoundVariants: [
    {
      centered: true,
      className: "px-10",
    },
  ],
});

function ConEdisonCardHeader({
  className,
  centered = false,
  ...props
}: BaseCardProps & VariantProps<typeof conEdisonCardHeaderVariants>) {
  return (
    <BaseCardHeader
      {...props}
      className={cn(conEdisonCardHeaderVariants(), className)}
    />
  );
}

const conEdisonCardTitleVariants = cva({
  base: [
    "text-3xl font-extrabold text-tenant-con-edison-primary-dark-gray md:text-[2rem]",
    "break-words", // fixes german translation on mobile (god damn wtaf is "Kredit-/Debitkarteninformationen")
    // lexend.className, // TODO: add lexend.className once available in this package
  ],
  variants: {
    centered: { true: "text-center", false: "text-left" },
  },
  compoundVariants: [
    {
      centered: true,
      className: "px-10",
    },
  ],
});

function ConEdisonCardTitle({
  className,
  centered = false,
  ...props
}: BaseCardProps & VariantProps<typeof conEdisonCardTitleVariants>) {
  return (
    <BaseCardTitle
      {...props}
      className={cn(conEdisonCardTitleVariants({ centered, className }))}
    />
  );
}

const conEdisonCardDescriptionVariants = cva({
  base: "w-fit pb-5 text-base font-normal text-black text-tenant-con-edison-primary-dark-gray",
  variants: {
    tenantVariant: {
      "oru-reco": null,
    },
  },
  compoundVariants: [
    {
      tenantVariant: "oru-reco",
      className: "text-tenant-oru-primary-dark-gray",
    },
  ],
});

function ConEdisonCardDescription({
  className,
  centered = false,
  tenant,
  ...props
}: BaseCardProps & VariantProps<typeof conEdisonCardTitleVariants>) {
  const tenantVariant =
    tenant?.variantId === "oru-reco" ? "oru-reco" : undefined;

  return (
    <BaseCardDescription
      {...props}
      className={cn(
        conEdisonCardDescriptionVariants({
          className,
          ...(tenantVariant && { tenantVariant }),
        }),
      )}
    />
  );
}

const conEdisonCardContentVariants = cva({
  base: "my-8 flex flex-col gap-8 px-5 md:px-10",
  variants: {
    centered: {
      true: "items-center justify-center",
      false: "items-start justify-start",
    },
  },
  compoundVariants: [
    {
      centered: true,
      className: "px-10",
    },
  ],
});

function ConEdisonCardContent({
  className,
  centered = false,
  tenant,
  ...props
}: BaseCardProps & VariantProps<typeof conEdisonCardTitleVariants>) {
  return (
    <BaseCardPanel
      {...props}
      className={cn(
        conEdisonCardContentVariants({
          className,
          centered,
        }),
      )}
    />
  );
}

const conEdisonCardFooterVariants = cva({
  base: "px-5 pb-[4.75rem] md:px-10",
  variants: {
    centered: { true: "justify-center", false: "justify-start" },
  },
  compoundVariants: [
    {
      centered: true,
      className: "px-10",
    },
  ],
});

function ConEdisonCardFooter({
  className,
  centered = false,
  tenant,
  ...props
}: BaseCardProps & VariantProps<typeof conEdisonCardFooterVariants>) {
  return (
    <BaseCardPanel
      {...props}
      className={cn(
        conEdisonCardFooterVariants({
          className,
          centered,
        }),
      )}
    />
  );
}

export const conEdisonCardKit: CardParts = {
  Root: ConEdisonCard,
  Header: ConEdisonCardHeader,
  Title: ConEdisonCardTitle,
  Description: ConEdisonCardDescription,
  Content: ConEdisonCardContent,
  Footer: ConEdisonCardFooter,
};
